// Plant Today (web) — a focused, single-crop "mission briefing". Mirrors the
// mobile Plant Today HUD: sow method for today, seed-depth scale, a companion
// friend/foe, a Handbook deep-link and a primary "Log as Planted" action.
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, ArrowRight, Sprout, Home, Move, BookOpen, Check, ThumbsUp, ThumbsDown, MapPin, CalendarClock,
} from 'lucide-react';
import Layout from '@/components/Layout';
import { cropImage } from '@/lib/cropImage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const C = {
  forest: '#1A4331', forestDeep: '#0F2D1F', cream: '#FAF6EC', creamWarm: '#F4EEDE', sage: '#E5EBDD',
  gold: '#C9A85B', goldTint: 'rgba(201,168,91,0.16)', lime: '#B4D330', leaf: '#6B9F4A', leafDeep: '#4E7A33', leafTint: 'rgba(107,159,74,0.16)',
  terracotta: '#C66B4A', terracottaTint: 'rgba(198,107,74,0.14)', text: '#2A3528', textMuted: '#5C665C', textSoft: '#7A6B57',
};
const METHOD_ICON = { direct: Sprout, seedling: Home, plant_out: Move };

function DepthScale({ cm, label }) {
  if (cm == null) return null;
  const planted = /^plant/i.test(label || '');
  const maxScale = cm <= 1 ? 2 : cm <= 2 ? 3 : cm <= 3 ? 5 : cm <= 5 ? 6 : cm <= 8 ? 10 : cm <= 10 ? 12 : 15;
  const SURF = 16, BOT = 150, usable = BOT - SURF;
  const markerY = SURF + (cm / maxScale) * usable;
  return (
    <div className="flex items-center gap-5">
      <div className="relative flex-shrink-0" style={{ width: 118, height: 160 }}>
        <div style={{ position: 'absolute', left: 30, top: SURF, bottom: 10, width: 18, borderRadius: 9, background: 'linear-gradient(to bottom,#CBA368,#9A6B3C 55%,#6E4725)' }} />
        <div style={{ position: 'absolute', left: 8, width: 62, top: SURF, height: 2, background: C.leaf, opacity: 0.55 }} />
        <span style={{ position: 'absolute', left: 54, top: SURF - 14, fontSize: 10, fontWeight: 700, color: C.textMuted }}>soil surface</span>
        <span style={{ position: 'absolute', left: 54, bottom: 0, fontSize: 10, fontWeight: 600, color: C.textSoft }}>{maxScale}cm</span>
        <motion.div initial={{ opacity: 0, top: SURF }} animate={{ opacity: 1, top: markerY - 9 }} transition={{ type: 'spring', stiffness: 200, damping: 20, delay: 0.15 }}
          className="flex items-center" style={{ position: 'absolute', left: 31 }}>
          <span style={{ width: 16, height: 19, background: '#5B3A1E', borderRadius: '50% 50% 50% 50% / 62% 62% 38% 38%', transform: 'rotate(14deg)', boxShadow: '0 2px 6px rgba(15,45,31,0.35)', border: '1.5px solid #3C2512' }} />
          <span style={{ height: 0, borderTop: `2px dashed ${C.gold}`, width: 26, marginLeft: 7 }} />
          <span style={{ marginLeft: 6, background: C.gold, color: '#fff', fontSize: 11, fontWeight: 700, borderRadius: 999, padding: '2px 9px', whiteSpace: 'nowrap' }}>{cm}cm</span>
        </motion.div>
      </div>
      <div className="flex-1">
        <p className="text-3xl font-bold leading-none" style={{ fontFamily: 'Outfit, sans-serif', color: C.forest }}>{cm}<span className="text-lg" style={{ color: C.textSoft }}> cm</span></p>
        <p className="text-sm font-semibold mt-1.5" style={{ color: C.leafDeep }}>{label}</p>
        <p className="text-[13px] mt-2 leading-relaxed" style={{ color: C.textSoft }}>
          {planted ? 'Set it in firmed soil and water in well.' : "Cover with fine soil and firm gently — roughly 2–3× the seed's width."}
        </p>
      </div>
    </div>
  );
}

function PlantToday({ user }) {
  const { cropId } = useParams();
  const navigate = useNavigate();
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logging, setLogging] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/plant-today/${cropId}`, { withCredentials: true })
      .then((r) => setBrief(r.data)).catch(() => setBrief(null)).finally(() => setLoading(false));
  }, [cropId]);

  const logPlanted = async () => {
    if (logging || done) return;
    setLogging(true);
    try {
      await axios.post(`${API}/plantings`, {
        crop_id: brief.crop_id, crop_name: brief.crop_name,
        sowing_date: new Date().toISOString(), days_to_harvest: brief.days_to_harvest,
        stage: 'seed', environment: 'outdoor',
      }, { withCredentials: true });
      setDone(true);
      setTimeout(() => navigate('/my-crops'), 900);
    } catch { setLogging(false); }
  };

  if (loading) {
    return <Layout user={user}><div className="flex items-center justify-center h-96"><div className="w-16 h-16 border-4 border-[#8EAA97] border-t-[#1A4331] rounded-full animate-spin" /></div></Layout>;
  }
  if (!brief) {
    return <Layout user={user}><div className="text-center py-12"><p style={{ color: C.textMuted }}>Crop not found</p><Link to="/season-plan" className="font-semibold mt-4 inline-block" style={{ color: C.forest }}>Back to planner</Link></div></Layout>;
  }

  const MethodIcon = METHOD_ICON[brief.sow_method?.key] || Sprout;
  const friend = brief.companion?.friend;
  const foe = brief.companion?.foe;

  return (
    <Layout user={user}>
      <div className="max-w-2xl mx-auto space-y-5 pb-10" data-testid="plant-today">
        <button onClick={() => navigate(-1)} data-testid="plant-today-back" className="inline-flex items-center gap-2" style={{ color: C.textMuted }}>
          <ArrowLeft className="w-4 h-4" /> <span className="font-medium">Back</span>
        </button>

        {/* Mission header */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ type: 'spring', damping: 22 }}
          className="hg-glass hg-float rounded-3xl p-6 flex items-center gap-4">
          <div className="rounded-2xl overflow-hidden flex-shrink-0" style={{ width: 76, height: 76, boxShadow: '0 10px 24px -14px rgba(15,45,31,0.6)' }}>
            <img src={cropImage(brief.crop_name, brief.category, brief.image_url)} alt={brief.crop_name} className="w-full h-full object-cover" data-testid="plant-today-image" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-bold uppercase" style={{ letterSpacing: '0.2em', color: C.leaf }}>Plant today</p>
            <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', color: C.forest }} data-testid="plant-today-name">{brief.crop_name}</h1>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {brief.zone_label && <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold" style={{ background: C.leafTint, color: C.leafDeep }}><MapPin className="w-3 h-3" /> {brief.zone_label}</span>}
              {(brief.weeks_from_seed || brief.weeks_to_maturity) && <span className="inline-flex items-center gap-1 text-[12px] font-semibold" style={{ color: C.textSoft }}><CalendarClock className="w-3.5 h-3.5" /> ~{brief.weeks_from_seed || brief.weeks_to_maturity} wks to harvest</span>}
            </div>
          </div>
        </motion.div>

        {/* Sow method today */}
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05, type: 'spring', damping: 22 }}
          className="hg-glass hg-float rounded-3xl p-6" data-testid="plant-today-method">
          <div className="flex items-start gap-4">
            <span className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: C.forest }}><MethodIcon className="w-6 h-6" style={{ color: C.lime }} /></span>
            <div className="flex-1">
              <p className="text-[11px] font-bold uppercase" style={{ letterSpacing: '0.16em', color: C.textMuted }}>Today's method · {brief.current_month}</p>
              <h2 className="text-xl font-bold mt-0.5" style={{ fontFamily: 'Outfit, sans-serif', color: C.forest }} data-testid="plant-today-method-title">{brief.sow_method?.title}</h2>
              <p className="text-sm mt-1.5 leading-relaxed" style={{ color: C.text }}>{brief.sow_method?.detail}</p>
            </div>
          </div>
        </motion.div>

        {/* Seed depth */}
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, type: 'spring', damping: 22 }}
          className="hg-glass hg-float rounded-3xl p-6" data-testid="plant-today-depth">
          <div className="flex items-center gap-2 mb-3">
            <span className="rounded-full" style={{ width: 16, height: 3, background: C.leafDeep }} />
            <span className="text-[11px] font-bold uppercase" style={{ letterSpacing: '0.18em', color: C.leafDeep }}>Seed Depth</span>
          </div>
          <DepthScale cm={brief.seed_depth_cm} label={brief.seed_depth_label} />
        </motion.div>

        {/* Companions */}
        {(friend || foe) && (
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, type: 'spring', damping: 22 }}
            className="hg-glass hg-float rounded-3xl p-6" data-testid="plant-today-companions">
            <div className="flex items-center gap-2 mb-3">
              <span className="rounded-full" style={{ width: 16, height: 3, background: C.gold }} />
              <span className="text-[11px] font-bold uppercase" style={{ letterSpacing: '0.18em', color: C.gold }}>Good company</span>
            </div>
            <div className="flex flex-wrap gap-2.5">
              {friend && (
                <span className="inline-flex items-center gap-2 rounded-full px-4 py-2.5" style={{ background: C.leafTint }} data-testid="plant-today-friend">
                  <ThumbsUp className="w-4 h-4" style={{ color: C.leafDeep }} />
                  <span className="text-sm"><span style={{ color: C.textSoft }}>Friend · </span><span className="font-bold" style={{ color: C.forest }}>{friend}</span></span>
                </span>
              )}
              {foe && (
                <span className="inline-flex items-center gap-2 rounded-full px-4 py-2.5" style={{ background: C.terracottaTint }} data-testid="plant-today-foe">
                  <ThumbsDown className="w-4 h-4" style={{ color: C.terracotta }} />
                  <span className="text-sm"><span style={{ color: C.textSoft }}>Keep apart · </span><span className="font-bold" style={{ color: C.terracotta }}>{foe}</span></span>
                </span>
              )}
            </div>
          </motion.div>
        )}

        {/* Handbook deep-link */}
        <Link to={`/crops/${brief.crop_id}`} data-testid="plant-today-handbook"
          className="hg-press hg-glass w-full flex items-center justify-center gap-2 rounded-full py-3.5 font-bold" style={{ color: C.forest }}>
          <BookOpen className="w-[18px] h-[18px]" style={{ color: C.leaf }} /> Open the Homegrown Handbook
        </Link>

        {/* Log as planted */}
        <button onClick={logPlanted} disabled={logging || done} data-testid="plant-today-log"
          className="hg-press hg-glow w-full flex items-center justify-center gap-2 rounded-full py-4 font-bold disabled:opacity-70"
          style={{ background: done ? C.leaf : C.forest, color: C.cream, boxShadow: `0 16px 34px -12px ${done ? C.leaf : 'rgba(26,67,49,0.6)'}` }}>
          {done ? <><Check className="w-5 h-5" /> Logged — nice work!</> : <><Sprout className="w-5 h-5" style={{ color: C.lime }} /> {logging ? 'Logging…' : 'Log as Planted'} <ArrowRight className="w-4 h-4" style={{ color: C.lime }} /></>}
        </button>
      </div>
    </Layout>
  );
}

export default PlantToday;
